import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import { trackMessageSubmission } from '../utils/analytics';

const MessagesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  margin: 0 0 3rem 0;
  
  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const MessageForm = styled(motion.form)`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const FormTitle = styled.h2`
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: ${props => props.theme.colors.dark};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.3s ease;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.secondary};
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 100%;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primary};
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.gray};
    cursor: not-allowed;
  }
`;

const MessagesSection = styled.div``;

const MessagesTitle = styled.h2`
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme.colors.success};
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const FinalUpdateContainer = styled(motion.div)`
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  border: 2px solid #38a169;
  border-radius: 16px;
  padding: 2rem;
  margin: 2rem 0 3rem 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const FinalUpdateHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const FinalUpdateTitle = styled.h2`
  color: #2f855a;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
`;

const FinalUpdateSubtitle = styled.p`
  color: #38a169;
  font-style: italic;
  margin-bottom: 0;
`;

const FinalUpdateContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;

const FinalUpdateText = styled.p`
  color: #2d3748;
  line-height: 1.8;
  font-size: 1.05rem;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FinalUpdateSignature = styled.div`
  text-align: right;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  color: #4a5568;
  font-style: italic;
`;

const CharacterCounter = styled.div`
  text-align: right;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.gray};
  margin-top: 0.25rem;
  
  &.warning {
    color: ${props => props.theme.colors.warning};
  }
  
  &.error {
    color: ${props => props.theme.colors.accent};
  }
`;

const Messages = () => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    // Basic client-side validation
    if (!formData.name.trim() || formData.name.length < 1 || formData.name.length > 100) {
      setSubmitStatus({
        type: 'error',
        message: 'Name must be between 1 and 100 characters.'
      });
      setSubmitting(false);
      return;
    }

    if (!formData.message.trim() || formData.message.length < 10 || formData.message.length > 1000) {
      setSubmitStatus({
        type: 'error',
        message: 'Message must be between 10 and 1000 characters.'
      });
      setSubmitting(false);
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitStatus({
        type: 'error',
        message: 'Please enter a valid email address.'
      });
      setSubmitting(false);
      return;
    }

    try {
      await axios.post('/api/messages', formData);
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your message! It will be reviewed before being posted.'
      });
      setFormData({ name: '', email: '', message: '' });
      
      // Track successful message submission
      trackMessageSubmission('community_message');
    } catch (error) {
      let errorMessage = 'Failed to submit message. Please try again.';
      
      if (error.response?.status === 429) {
        errorMessage = 'Too many submissions. Please wait before submitting another message.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data.error || 'Please check your input and try again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      setSubmitStatus({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MessagesContainer>
      <PageHeader>
        <PageTitle>Community Messages</PageTitle>
        <PageSubtitle>
          Share your thoughts, prayers, and support for the Soward family. 
          Your words mean everything during this miraculous journey.
        </PageSubtitle>
      </PageHeader>

      <FinalUpdateContainer
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <FinalUpdateHeader>
          <FinalUpdateTitle>Final Update from Scott Soward</FinalUpdateTitle>
          <FinalUpdateSubtitle>Posted after Maggie came home from the hospital</FinalUpdateSubtitle>
        </FinalUpdateHeader>
        
        <FinalUpdateContent>
          <FinalUpdateText>
            Last and hopefully final update on our crazy week. Maggie came home yesterday at 3 o'clock, 
            and it was so wonderful to be with the kids and in our own home. There were paper hearts with 
            messages all over the garage door, front door, chalk drawings on the drive, and flowers and 
            gifts in our home. It was a wonderful welcome home for Maggie and me. ❤️🙏
          </FinalUpdateText>
          
          <FinalUpdateText>
            It is impossible to summarize at this point what we experienced the past seven days, and I 
            suspect we'll need some time to process. But Maggie is doing good, the device they put in her 
            chest is causing her pain as she heals, and she'll also be adjusting to the cocktail of medicines 
            she'll be taking to help her heart recover and get stronger. But she is alive and we are home!
          </FinalUpdateText>
          
          <FinalUpdateText>
            Our time in the hospital produced all the emotions. Day one, we were so scared she would not 
            pull through, and then deeply concerned that she might have brain damage from the lack of blood 
            and oxygen to the brain during the cardiac arrest (what Maggie likes to call her death). Then 
            relieved on Friday when she raised her eyebrows and wiggled her toes on command because we knew 
            she was still in there.
          </FinalUpdateText>
          
          <FinalUpdateText>
            She finally woke up Friday night and talking to her while she was still seriously sedated was 
            hilarious. She could not remember anything from five minutes to the next so she asked the same 
            questions on repeat. Her sisters and all the kids were there. She said the funniest things, 
            flirting with Andrew the nurse and embarrassing Declan. We all laughed and loved her up. She 
            also said the most loving and endearing things, and we shared tears of joy as she told us 
            (repeatedly) of her love for us and her love for Jesus.
          </FinalUpdateText>
          
          <FinalUpdateText>
            We are all recovering, and we'll work together to get through this time. But we are so grateful 
            to all of you who have reached out, and shared your love with us, your time, flowers and gifts, 
            messages, and service taking care of our kids. Please know that it was a great comfort knowing 
            that while Maggie and I were in the hospital, our kids were loved and taken care of. Thank you. ❤️
          </FinalUpdateText>
          
          <FinalUpdateText>
            Also, I cannot express how grateful we are to our loving Heavenly Father who allowed Maggie to 
            stay with us and arranged for the perfect scenario where she was preserved and survived this 
            terrifying experience. We have felt His love and mercy and peace. I am amazed and terrified when 
            I think of how differently the outcome would have been with a minor change to just a small detail 
            of the events on Thanksgiving day. She died and came back to us thanks to Him. We have seen miracles.
          </FinalUpdateText>
          
          <FinalUpdateText>
            And I know there's many things that happened prior to and on that day that we are not aware of 
            that provided for Maggie's rescue. We are grateful for the emergency workers, EMT's firefighters 
            and police, for their training and decision to have an occupation that literally saves and changes 
            lives. We're so grateful to the hospital staff, nurses doctors, cardiologists that have elected 
            to work and to serve their fellow human beings and save lives.
          </FinalUpdateText>
          
          <FinalUpdateText>
            We are grateful for family, for blessings, and the power of prayer. We are so grateful for all 
            of you who have reached out and shared with us your love for Maggie and our family, and provided 
            for us in so many ways. My heart breaks for those of you who have gone through similar situations 
            with less desirable outcomes. I'm so sorry for your pain and your loss. We love you all and wish you peace.
          </FinalUpdateText>
          
          <FinalUpdateText>
            We are all in God's hands and His love for us is poured out through the hands of earthly and 
            heavenly angels. Thank you all!
          </FinalUpdateText>
          
          <FinalUpdateSignature>
            — Scott Soward, on behalf of the Soward Family
          </FinalUpdateSignature>
        </FinalUpdateContent>
      </FinalUpdateContainer>

      <ContentGrid>
        <MessageForm
          onSubmit={handleSubmit}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <FormTitle>Leave a Message</FormTitle>
          
          {submitStatus && (
            submitStatus.type === 'success' ? (
              <SuccessMessage>{submitStatus.message}</SuccessMessage>
            ) : (
              <ErrorMessage>{submitStatus.message}</ErrorMessage>
            )
          )}

          <FormGroup>
            <Label htmlFor="name">Name *</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Your name"
              maxLength="100"
            />
            <CharacterCounter
              className={
                formData.name.length > 100 ? 'error' : 
                formData.name.length > 90 ? 'warning' : ''
              }
            >
              {formData.name.length}/100
            </CharacterCounter>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email (optional)</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="message">Message *</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              placeholder="Share your thoughts, prayers, or words of encouragement..."
              maxLength="1000"
            />
            <CharacterCounter
              className={
                formData.message.length > 1000 ? 'error' : 
                formData.message.length > 900 ? 'warning' : ''
              }
            >
              {formData.message.length}/1000
            </CharacterCounter>
          </FormGroup>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send Message'}
          </SubmitButton>
        </MessageForm>

        <div>
          <MessagesSection>
            <MessagesTitle>Messages of Support</MessagesTitle>
            
            <div style={{ 
              padding: '2rem', 
              backgroundColor: '#f7fafc', 
              borderRadius: '12px', 
              border: '1px solid #e2e8f0',
              textAlign: 'center'
            }}>
              <p style={{ 
                margin: '0 0 1rem 0', 
                fontSize: '1.1rem', 
                color: '#2d3748', 
                fontWeight: 'bold' 
              }}>
                💬 Community Messages Now in Footer
              </p>
              <p style={{ 
                margin: '0 0 1rem 0', 
                fontSize: '0.95rem', 
                color: '#4a5568', 
                lineHeight: '1.6' 
              }}>
                Your messages of support are now displayed in the footer of every page on our website! 
                This way, your kind words and prayers will be seen by everyone who visits, 
                creating a constant reminder of our amazing community's love and support.
              </p>
              <p style={{ 
                margin: 0, 
                fontSize: '0.9rem', 
                color: '#718096',
                fontStyle: 'italic'
              }}>
                Scroll down to the bottom of any page to see the latest community messages.
              </p>
            </div>
          </MessagesSection>
        </div>
      </ContentGrid>

      <FinalUpdateContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <FinalUpdateHeader>
          <FinalUpdateTitle>🌟 A Message from the Soward Family 🌟</FinalUpdateTitle>
          <FinalUpdateSubtitle>Thank you for your overwhelming support!</FinalUpdateSubtitle>
        </FinalUpdateHeader>

        <FinalUpdateContent>
          <FinalUpdateText>
            Dear friends and family, 
            <br /><br />
            As we close this chapter, I am filled with gratitude for each and every one of you. 
            Your messages, prayers, and support have been a source of strength for our family.
          </FinalUpdateText>

          <FinalUpdateText>
            We are excited to share that we have created a dedicated space on our website to 
            continue this journey together. Please visit our <a href="/updates" style={{ color: '#38a169', fontWeight: 'bold' }}>Updates Page</a> for the latest news and stories.
          </FinalUpdateText>

          <FinalUpdateText>
            Although this is the final message in this thread, your voices will continue to be 
            heard and cherished. Thank you for being part of our community.
          </FinalUpdateText>

          <FinalUpdateSignature>
            With all our love and gratitude,<br />
            The Soward Family
          </FinalUpdateSignature>
        </FinalUpdateContent>
      </FinalUpdateContainer>
    </MessagesContainer>
  );
};

export default Messages;
